import api from '.';
import debug from '../debug';
import {
  decrypt,
  encrypt,
  generateSessionKey,
  encryptEnvelope,
  decryptEnvelope,
  encryptSessionKey,
  isCertificateUsable,
  getCertificateFailureReason,
} from '../secure';
import { v4 as uuidv4 } from 'uuid';
import {
  APP_ID,
  APP_VERSION,
  getXApiKeyByEnv,
} from '../constants';
import DataLakeTracker from '../tracking/data-lake-tracker';

const authorizationExcludedRoutes = [
  'appclicknew',
  'sendotplogin',
  'verifyotplogin',
  'customeronboarding/getcertificatebyorigin',
  'dashboardmenus/sso/validate-token',
  'hypervergeToken',
  // DOCS: Limit Enhancement 2.0 routes
  'validatetoken',
  'perfiosforweb',
  'getbankforweb',
  'getprefferedoptionofbankfoeweb',
  's3savefile-old',
  'docuploadforweb',
  // DOCS: GPay routes
  'customeronboarding/createcustomer',
];

/**
 * Routes that must keep using the legacy AES scheme even when envelope
 * encryption is enabled. These run before a certificate can be trusted, or the
 * backend only understands the legacy format for them.
 */
const LEGACY_ONLY_ROUTES = ['customeronboarding/getcertificatebyorigin'];

/**
 * Error handed to `errorFn` when a request cannot be encrypted because there is
 * no usable certificate. Carries a `code` so call sites and support can tell it
 * apart from an HTTP failure - it never reached the network.
 */
const certificateUnavailableError = (reason, route) => {
  const error = new Error(
    `[SECURE-CLIENT] Request to /${route} blocked: ${reason}`
  );
  error.code = 'ENVELOPE_CERTIFICATE_UNAVAILABLE';
  error.reason = reason;
  return error;
};

// Reported once per page load rather than on every call site. This fires when
// the certificate has not arrived yet, or is expired/unparseable - in every
// case the request is BLOCKED rather than downgraded (see below).
let certificateFailureReported = false;

const reportCertificateFailure = (route) => {
  if (certificateFailureReported) return;
  certificateFailureReported = true;

  const reason = getCertificateFailureReason() || 'CERTIFICATE_UNAVAILABLE';
  debug(
    'ERROR',
    `[SECURE-CLIENT] Envelope unavailable, blocking request: ${reason}`
  );

  try {
    DataLakeTracker({
      logEventType: 'error',
      eventName: 'ENVELOPE_ENCRYPTION_UNAVAILABLE',
      eventValue: `${reason} | route=${route}`,
    });
  } catch (err) {
    console.log('Failed to report certificate failure: ', err);
  }
};

/**
 * API Client for Simple and Encrypt-Decrypt API calls
 * @param {boolean} isEncrypted - true if the API call is encrypted
 * @param {boolean} isExternalBaseUrl - true if the API endpoint is external than conventional API endpoint
 * @param {string} externalBaseUrl - full endpoint URL like https://example.com/some-random-api
 * @param {number} customerId - used for key generation used for encryption and decryption, 'null' for intial API calls like verifyOtp, sendOtp, etc
 * @param {string} route - API route like api/v1/verifyOtp (without base URL,Without leading slash)
 * @param {object} payload - Payload to be posted to the API Endpoint (In encrypted API's case, payload will be stringified and encrypted)
 * @param {object} headers - Headers used to pass as the request headers like (authorization, customerId, Authorization etc)
 * @param {boolean} otherPayloadDetailsPresent - true if the payload contains other details like requestId, appVersion, appId, etc (only true for encrypted API's)
 * @param {string} method - "GET" or "POST" or "PUT" or "DELETE"
 * @param {Function} successFn - Single param callback takes decrypted response object as param
 * @param {Function} errorFn -  Single param callback takes error object as param
 * @param {Function} finallyFn - No param, finally clause in API call
 */
const sendSecureRequest = ({
  isEncrypted = false,
  isExternalBaseUrl = false,
  externalBaseUrl = '',
  customerId = null,
  route = '',
  payload = {},
  otherPayloadDetailsPresent = false,
  headers = {},
  method = 'POST',
  useLegacyEncryption = false,
  successFn = (res) => {
    debug('WATCH', `Response: `, res);
  },
  errorFn = (err) => {
    debug('ERROR', `Error: `, err);
  },
  finallyFn = () => {
    debug('WATCH', `Finally: `);
  },
}) => {
  const apiLoadingStarted = new Date().getTime();
  let APIPayloadToPost = {},
    APIEndptToPost = route;

  // Scheme selection happens here and nowhere else. When `useEnvelope` is
  // false the request is byte-for-byte what it was before this change.
  const isLegacyOnlyRoute = LEGACY_ONLY_ROUTES.includes(route);
  const envelopeRequested =
    isEncrypted &&
    !useLegacyEncryption &&
    !isLegacyOnlyRoute;
  const useEnvelope = envelopeRequested && isCertificateUsable();

  // HARD FAIL: a request that should be envelope-encrypted is never sent under
  // the weaker legacy scheme just because the certificate is missing. Silently
  // downgrading would put every customer back on the single shared key that
  // this whole change exists to remove, with nothing visible to say so.
  //
  // This only ever blocks envelope-eligible requests. Unencrypted calls, the
  // bootstrap routes in LEGACY_ONLY_ROUTES, explicit `useLegacyEncryption`
  // opt-outs, and everything at all when the env flag is off are unaffected -
  // so turning the flag off remains a complete rollback.
  if (envelopeRequested && !useEnvelope) {
    reportCertificateFailure(route);
    const reason = getCertificateFailureReason() || 'CERTIFICATE_UNAVAILABLE';
    errorFn(certificateUnavailableError(reason, route));
    if (finallyFn) finallyFn();
    return;
  }

  // Held for the lifetime of this call only: the response is decrypted with
  // the same key inside the closure below. Never stored, logged or shared.
  let sessionKey = null;
  const cryptoHeaders = {};

  if (isEncrypted) {
    // const encryptedPayload = {
    //   data: encrypt(customerId, payload),
    // };
    let encryptedPayload;
    try {
      if (useEnvelope) {
        sessionKey = generateSessionKey();
        encryptedPayload = { data: encryptEnvelope(sessionKey, payload) };
        cryptoHeaders.uuid_wp = encryptSessionKey(sessionKey);
      } else if (isLegacyOnlyRoute) {
        encryptedPayload = { data: encrypt(customerId, payload) };
      }
    } catch (err) {
      // Never fall through to sending an unencrypted body.
      debug('ERROR', `[SECURE-CLIENT] Encryption failed for /${route}`, err);
      errorFn(err);
      if (finallyFn) finallyFn();
      return;
    }
    if (otherPayloadDetailsPresent) {
      encryptedPayload['appId'] = APP_ID;
      encryptedPayload['appVersion'] = APP_VERSION;
      encryptedPayload['requestId'] = uuidv4();
    }
    APIPayloadToPost = encryptedPayload;
  } else {
    APIPayloadToPost = payload;
  }
  if (isExternalBaseUrl) {
    APIEndptToPost = externalBaseUrl;
  }
  try {
    const { authorization = null, Authorization = null } = headers;
    if (
      !Authorization &&
      !authorization &&
      !authorizationExcludedRoutes.includes(route)
    ) {
      debug('ERROR', `[SECURE-CLIENT] Authorization null, 401 Error`);
    }
  } catch (err) {}
  debug('REQUEST', ` ${method} /${route} `, payload);

  // Kept on both schemes: the response interceptor in `./index.js` reads this
  // back to attach a customerId to datalake error events.

  localStorage.setItem(
    `custId_${route.split('/').pop()}`,
    customerId ? customerId : null
  );

  api(APIEndptToPost, APIPayloadToPost, method, {
    ...headers,
    ...cryptoHeaders,
    'x-api-key': getXApiKeyByEnv(),
  })
    .then((res) => {
      let decrypted = {};

      if (isEncrypted) {
        console.log('resEncStr: ', res.data);
        let resEncStr = res.data && res.data.response;

        debug('RESPONSE', `ENC ${method} /${route} :`, {
          resEncStr,
          customerId,
        });
        // decrypted = decrypt(customerId, resEncStr);
        try {
          if (sessionKey){
            decrypted = decryptEnvelope(sessionKey, resEncStr);
          } else if(isLegacyOnlyRoute) {
            decrypted = decrypt(customerId, resEncStr);
          }
        } catch (error) {
          console.log('[SECURE-CLIENT] Response decryption failed', {
            method,
            route,
            customerId,
            resEncStr,
            error,
          });

          try {
            DataLakeTracker({
              logEventType: 'error',
              eventName: 'DECRYPTION_FAILURE',
              eventValue: `${
                error?.message || String(error || 'Unknown error')
              } | route=${route}}`,
              Authorization: null,
              customerId: null,
              customerRefNum: null,
            });
          } catch (trackerErr) {
            console.log('Failed to report decryption failure: ', trackerErr);
          }

          errorFn(error);
          return;
        }
        try {
          const { statusCode, statusDes, statusMsg } = decrypted;
          if (statusCode >= 3000 && statusCode <= 3999) {
            const inactiveCustomerError = {
              statusCode,
              statusDes,
              statusMsg,
            };
            localStorage.setItem(
              'inactiveCustomerError',
              JSON.stringify(inactiveCustomerError)
            );
            window.location = '/inactive-customer';
          }
        } catch (err) {
          console.log('Inactive Customer [3000-3999]: ', err);
        }

        debug('RESPONSE', `DEC ${method} /${route} `, decrypted);
      } else {
        decrypted = res.data;
        debug('RESPONSE', `NON ${method} /${route} `, decrypted);
      }
      successFn(decrypted);
    })
    .catch((err) => {
      debug('ERROR', err);
      console.log('err: ', err);
      const res = err?.response;
      if (res && res?.status === 503) {
        // DOCS: Check for disaster recovery changes here
        const disasterRecoveryState = {
          statusCode: res?.status,
          statusMsg: res?.data?.statusMsg,
          statusDes: res?.data?.statusDes,
        };
        localStorage.setItem(
          'disasterRecoveryState',
          JSON.stringify(disasterRecoveryState)
        );
        window.location = '/under-maintainance';
      }

      errorFn(err);
    })
    .finally(() => {
      const apiLoadingFinished = new Date().getTime();
      const timeTakenByAPI = apiLoadingFinished - apiLoadingStarted;
      debug('WATCH', `END ${method} /${route}`, timeTakenByAPI, 'ms');
      if (finallyFn) finallyFn();
    });
};

/**
 * How long a request will wait for the certificate before giving up. A hung
 * getcertificate must not hang the customer's first API call indefinitely.
 */
const CERTIFICATE_WAIT_TIMEOUT_MS = 8000;

/**
 * Loads the certificate fetcher at call time rather than import time.
 * `fetch-certificate.js` imports this module, so a static import here would be
 * a cycle. Deferring it to the moment it is needed breaks that.
 */
const fetchCertificateLazily = () =>
  import('../secure/fetch-certificate').then(({ ensureCertificate }) =>
    ensureCertificate()
  );

/**
 * Fetches the certificate, then runs `send` — whatever the outcome, including
 * on timeout. `send` re-evaluates the scheme: it encrypts with the envelope if
 * the fetch landed a usable certificate, and blocks the request if it did not.
 */
const afterCertificate = (route, send) => {
  let alreadySent = false;
  const sendOnce = () => {
    if (alreadySent) return;
    alreadySent = true;
    send();
  };

  const timer = setTimeout(() => {
    debug('ERROR', `[SECURE-CLIENT] certificate wait timed out for /${route}`);
    sendOnce();
  }, CERTIFICATE_WAIT_TIMEOUT_MS);

  fetchCertificateLazily()
    .catch((err) => {
      debug(
        'ERROR',
        `[SECURE-CLIENT] certificate fetch failed for /${route}`,
        err
      );
    })
    .finally(() => {
      clearTimeout(timer);
      sendOnce();
    });
};

/**
 * API client. Thin wrapper over `sendSecureRequest` that makes sure a
 * certificate exists before an envelope-encrypted request goes out.
 *
 * A customer landing on the app for the first time (or after storage was
 * cleared) has no certificate in redux yet. That is not a failure on its own —
 * it is the trigger to fetch one. The request waits, then goes out properly
 * encrypted. It is only blocked if the fetch fails to produce a usable
 * certificate. Everything else is passed straight through.
 *
 * `SecureClient` has always been fire-and-forget — it returns nothing and
 * reports through `successFn`/`errorFn` — so deferring the send is invisible to
 * every call site.
 */
const SecureClient = (options = {}) => {
  const {
    isEncrypted = false,
    route = '',
    useLegacyEncryption = false,
  } = options;

  // `LEGACY_ONLY_ROUTES` contains the getcertificate route itself, so this can
  // never recurse into fetching a certificate in order to fetch a certificate.
  const needsCertificate =
    isEncrypted &&
    !useLegacyEncryption &&
    !LEGACY_ONLY_ROUTES.includes(route);

  if (needsCertificate && !isCertificateUsable()) {
    afterCertificate(route, () => sendSecureRequest(options));
    return;
  }

  sendSecureRequest(options);
};

export default SecureClient;
